import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { physicalSitesApi, peopleApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { toneFromMap, type Tone } from '../constants/tone';

/**
 * Identity colours per location type — not a severity scale (unlike Classification/Criticality
 * elsewhere), just enough visual variety that a table of site types doesn't read as monochrome.
 * Same idea as DocumentsList.tsx's DOC_TYPE_COLOURS. Grouped loosely by role: the one HQ stands
 * out; data/tech infrastructure, logistics/industrial sites, and customer/innovation-facing sites
 * each share a colour; Disaster Recovery gets its own since it's worth noticing at a glance.
 */
const LOCATION_TYPE_TONE: Record<string, Tone> = {
  Headquarters: 'primary',
  'Data Center': 'info',
  'Co-location Facility': 'info',
  'Server Room': 'info',
  Warehouse: 'dark',
  'Distribution Center': 'dark',
  'Manufacturing Plant': 'dark',
  'Research & Development Lab': 'success',
  'Retail Store': 'success',
  'Call Center': 'success',
  'Disaster Recovery Site': 'warning',
  'Regional Office': 'neutral',
  'Branch Office': 'neutral',
  'Training Facility': 'neutral',
};

type PhysicalSite = {
  siteId: string;
  name?: string;
  locationType?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postCode?: string;
  country?: string;
  primaryContactId?: string | null;
  notes?: string;
};

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
};



const getPersonLabel = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId).trim();
};

const formatAddress = (site: PhysicalSite): string => {
  const parts = [site.addressLine1, site.city, site.country].filter(Boolean);
  return parts.join(', ') || '-';
};

export const PhysicalSitesPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: sites = [], isLoading, error } = useQuery<PhysicalSite[]>({
    queryKey: ['physicalSites'],
    queryFn: physicalSitesApi.getAll,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const [search, setSearch] = useState('');

  const deleteMutation = useMutation({
    mutationFn: (site: PhysicalSite) => physicalSitesApi.delete(site.siteId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['physicalSites'] });
      toast.success('Site deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (site: PhysicalSite) => {
    const ok = await swal.confirmDelete(site.name || site.siteId);
    if (!ok) return;
    deleteMutation.mutate(site);
  };

  const personById = useMemo(() => {
    const map = new Map<string, Person>();
    people.forEach((p) => map.set(p.personId, p));
    return map;
  }, [people]);

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((site) => {
      const contact = site.primaryContactId ? personById.get(site.primaryContactId) : undefined;
      return [site.name || site.siteId, formatAddress(site), contact ? getPersonLabel(contact) : '']
        .some((v) => v?.toLowerCase().includes(q));
    });
  }, [sites, search, personById]);

  type SortField = 'name' | 'locationType' | 'address' | 'contact';

  const { sorted: sortedSites, field: sortField, dir: sortDir, toggle: handleSort } = useSortableData<PhysicalSite, SortField>(
    searched,
    (site, field) => {
      switch (field) {
        case 'name':
          return site.name || site.siteId;
        case 'locationType':
          return site.locationType || '';
        case 'address':
          return formatAddress(site);
        case 'contact': {
          const contact = site.primaryContactId ? personById.get(site.primaryContactId) : undefined;
          return contact ? getPersonLabel(contact) : '';
        }
      }
    },
    'name',
  );

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-map-pin-2-line"
        title="Physical Sites"
        colour="warning"
        actions={
          <button className="np-btn np-btn-primary" onClick={() => navigate('/physical-sites/new')}>
            <i className="ri-add-line"></i>Add Site
          </button>
        }
      />

      <div className="np-card">
        <div className="np-card-body border-bottom py-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 280 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search name, address, contact…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            {search && (
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setSearch('')}>
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub np-mb-0 ms-auto">{sortedSites.length} result{sortedSites.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger m-3 mb-0"><i className="ri-error-warning-line"></i>Failed to load physical sites.</div>
          ) : sortedSites.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-map-pin-2-line ri-48px d-block mb-3" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">{sites.length === 0 ? 'No sites found.' : 'No results match your filters.'}</p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="name" active={sortField === 'name'} dir={sortDir} onSort={handleSort} width="220px">Name</SortableTh>
                    <SortableTh field="locationType" active={sortField === 'locationType'} dir={sortDir} onSort={handleSort} width="160px">Type</SortableTh>
                    <SortableTh field="address" active={sortField === 'address'} dir={sortDir} onSort={handleSort}>Address</SortableTh>
                    <SortableTh field="contact" active={sortField === 'contact'} dir={sortDir} onSort={handleSort} width="200px">Primary Contact</SortableTh>
                    <th style={{ width: '96px' }}></th>
                  </tr>}>
                  {sortedSites.map((site) => {
                    const contact = site.primaryContactId ? personById.get(site.primaryContactId) : undefined;
                    return (
                      <tr key={site.siteId}>
                        <td className="np-row-title-static">{site.name || site.siteId}</td>
                        <td>
                          {site.locationType ? (
                            <span className={`np-chip np-tone-${toneFromMap(LOCATION_TYPE_TONE, site.locationType, 'neutral')}`}>{site.locationType}</span>
                          ) : (
                            <span style={{ color: 'var(--bs-secondary-color)' }}>-</span>
                          )}
                        </td>
                        <td className="np-hint">{formatAddress(site)}</td>
                        <td>{contact ? getPersonLabel(contact) : '-'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={() => navigate(`/physical-sites/${site.siteId}/edit`)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => handleDelete(site)}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
        </div>
      </div>
    </div>
  );
};

